import { DocumentReference, Transaction, doc, runTransaction } from 'firebase/firestore'
import { db } from '../store/firebase'
import { User } from '../types'
import {
  BillLine,
  BillingAccount,
  BillingConfig,
  BillingResult,
  ChargeClaim,
  RecurringFrequency,
  RecurringInvoice,
  SalesDocType,
  SalesDocument,
} from '../types/billing'
import { BCOL, clean, commitNumbers, failure, planNumbers, writeAudit } from './billingFirestore'
import { addDays, calculateLine, docLabel, documentTotals, splitLines, toN, todayLusaka } from './billingMoney'

export type DocumentInput = {
  /** Existing document id, or a stable new id derived from the editor's client key (makes retries safe). */
  id: string
  type: SalesDocType
  account: BillingAccount
  /** Patient covered when the account is a company or insurer. */
  patient?: { id: string; name: string; bmcFileNumber: string }
  encounterId?: string
  encounterLabel?: string
  date: string
  dueDate?: string
  validUntil?: string
  paymentTerms?: string
  notes?: string
  footer?: string
  lines: BillLine[]
  split?: { payer: BillingAccount; percent: number }
}

const entityFor = (type: SalesDocType) => (type === 'invoice' ? 'Invoice' : type === 'quotation' ? 'Quotation' : 'Order') as 'Invoice' | 'Quotation' | 'Order'
const docRefOf = (id: string) => doc(db, BCOL.documents, id)
const claimRef = (sourceRef: string) => doc(db, BCOL.claims, sourceRef.replace(/\//g, '_'))
const accountRef = (id: string) => doc(db, BCOL.accounts, id)

function validateLines(lines: BillLine[], forIssue: boolean) {
  if (lines.length === 0) throw new Error('Add at least one item or service.')
  lines.forEach((line, i) => {
    if (!line.description.trim()) throw new Error(`Line ${i + 1} needs a description.`)
    if (!line.fixed && !(line.quantity > 0)) throw new Error(`Line ${i + 1}: quantity must be greater than zero.`)
    if (!line.fixed && line.unitPrice < 0) throw new Error(`Line ${i + 1}: unit price cannot be negative.`)
  })
  const seen = new Set<string>()
  lines.forEach((line) => {
    if (!line.sourceRef) return
    if (seen.has(line.sourceRef)) throw new Error(`"${line.description}" appears twice. Each hospital charge can be billed once.`)
    seen.add(line.sourceRef)
  })
  if (forIssue && toN(documentTotals(lines).total) <= 0) throw new Error('An issued invoice must have a total greater than zero.')
}

function patientFields(account: BillingAccount, patient?: DocumentInput['patient']) {
  if (account.type === 'Patient') return { patientId: account.patientId, patientName: account.name, bmcFileNumber: account.bmcFileNumber }
  if (patient) return { patientId: patient.id, patientName: patient.name, bmcFileNumber: patient.bmcFileNumber }
  return {}
}

/** Recalculates every line and totals from raw inputs inside the transaction — UI totals are never trusted. */
function priced(lines: BillLine[]) {
  const recalculated = lines.map((line) => calculateLine(line))
  return { lines: recalculated, ...documentTotals(recalculated) }
}

async function ensureAccount(tx: Transaction, account: BillingAccount) {
  const snap = await tx.get(accountRef(account.id))
  return snap.exists()
}

/**
 * Save a draft, save a quotation/order, or issue an invoice. One transaction:
 * re-prices lines, reserves numbers, claims hospital charge references so they
 * can never be billed twice, and writes the audit trail.
 */
export async function saveSalesDocument(
  user: User,
  config: BillingConfig,
  input: DocumentInput,
  issue: boolean,
): Promise<BillingResult> {
  try {
    const now = new Date().toISOString()
    const isInvoice = input.type === 'invoice'
    const splitting = Boolean(isInvoice && issue && input.split && input.split.percent > 0 && input.split.percent < 100)
    validateLines(input.lines, issue && isInvoice)
    if (isInvoice && issue && input.split && !splitting) throw new Error('Payer cover must be between 1% and 99%.')
    if (splitting && input.split!.payer.id === input.account.id) throw new Error('Choose a different payer for the split.')

    const result = await runTransaction(db, async (tx) => {
      // ── READS ──
      const ref = docRefOf(input.id)
      const payerId = `${input.id}-payer`
      const existingSnap = await tx.get(ref)
      const existing = existingSnap.exists() ? (existingSnap.data() as SalesDocument) : null
      if (existing) {
        if (existing.type === 'invoice' && existing.state !== 'Draft') {
          return { id: existing.id, already: true, number: existing.number }
        }
        if (existing.type === 'quotation' && existing.state === 'Converted') throw new Error('This quotation has already been converted.')
        if (existing.type === 'order' && existing.state !== 'Open') throw new Error(`This order is ${existing.state.toLowerCase()}.`)
      }
      const accountExists = await ensureAccount(tx, input.account)
      const payerExists = splitting ? await ensureAccount(tx, input.split!.payer) : true

      const refs = Array.from(new Set(input.lines.map((l) => l.sourceRef).filter(Boolean) as string[]))
      const releasedRefs = (existing?.lines ?? []).map((l) => l.sourceRef).filter((r): r is string => Boolean(r) && !refs.includes(r!))
      const claimSnaps = await Promise.all(refs.map((r) => tx.get(claimRef(r))))
      claimSnaps.forEach((snap, i) => {
        const claim = snap.data() as ChargeClaim | undefined
        if (claim && claim.invoiceId !== input.id && claim.invoiceId !== payerId) {
          const line = input.lines.find((l) => l.sourceRef === refs[i])
          throw new Error(`"${line?.description ?? refs[i]}" is already billed on ${claim.invoiceNumber ?? 'a draft invoice'}.`)
        }
      })
      const releasedSnaps = await Promise.all(releasedRefs.map((r) => tx.get(claimRef(r))))

      const series = input.type === 'invoice' ? 'invoice' : input.type === 'quotation' ? 'quotation' : 'order'
      const needsNumber = isInvoice ? issue : !existing?.number
      const plan = needsNumber ? await planNumbers(tx, config, series, splitting ? 2 : 1) : null

      // ── WRITES ──
      if (!accountExists) tx.set(accountRef(input.account.id), clean({ ...input.account, createdAt: now, createdByName: user.fullName }))
      if (splitting && !payerExists) tx.set(accountRef(input.split!.payer.id), clean({ ...input.split!.payer, createdAt: now, createdByName: user.fullName }))

      const { lines, subtotal, discountTotal, taxTotal, total } = priced(input.lines)
      const issuer = issue ? { business: config.business, appearance: config.appearance, regional: config.regional } : existing?.issuer
      const base = {
        type: input.type,
        accountId: input.account.id,
        accountName: input.account.name,
        accountType: input.account.type,
        ...patientFields(input.account, input.patient),
        encounterId: input.encounterId,
        encounterLabel: input.encounterLabel,
        date: input.date,
        dueDate: isInvoice ? input.dueDate || input.date : undefined,
        validUntil: input.type === 'quotation' ? input.validUntil : undefined,
        paymentTerms: input.paymentTerms,
        notes: input.notes,
        footer: input.footer,
        staffId: existing?.staffId ?? user.id,
        staffName: existing?.staffName ?? user.fullName,
        amountPaid: 0,
        amountCredited: 0,
        convertedFromId: existing?.convertedFromId,
        convertedFromNumber: existing?.convertedFromNumber,
        recurringId: existing?.recurringId,
        createdAt: existing?.createdAt ?? now,
        createdByName: existing?.createdByName ?? user.fullName,
        updatedAt: now,
      }
      const state = isInvoice ? (issue ? 'Issued' : 'Draft') : input.type === 'quotation' ? existing?.state ?? 'Draft' : 'Open'

      let mainNumber: string | null = existing?.number ?? null
      if (plan) {
        mainNumber = plan.numbers[0]
        commitNumbers(tx, plan, splitting ? [input.id, payerId] : [input.id])
      }

      if (splitting) {
        const percent = input.split!.percent
        const [payerLines, patientLines] = splitLines(lines, percent)
        const groupId = input.id
        const payerNumber = plan!.numbers[1]
        const patientTotals = documentTotals(patientLines)
        const payerTotals = documentTotals(payerLines)
        const patientDoc: SalesDocument = {
          ...base, id: input.id, number: mainNumber, state: 'Issued', lines: patientLines, ...patientTotals, balance: patientTotals.total,
          split: { groupId, role: 'Patient', percent: 100 - percent, counterpartId: payerId, counterpartNumber: payerNumber },
          issuer, issuedAt: now, issuedByName: user.fullName,
        } as SalesDocument
        const payerDoc: SalesDocument = {
          ...base, id: payerId, number: payerNumber, state: 'Issued', accountId: input.split!.payer.id, accountName: input.split!.payer.name,
          accountType: input.split!.payer.type, ...patientFields(input.split!.payer, input.account.type === 'Patient' ? { id: input.account.patientId!, name: input.account.name, bmcFileNumber: input.account.bmcFileNumber ?? '' } : input.patient),
          lines: payerLines, ...payerTotals, balance: payerTotals.total,
          split: { groupId, role: 'Payer', percent, counterpartId: input.id, counterpartNumber: mainNumber ?? undefined },
          issuer, issuedAt: now, issuedByName: user.fullName, createdAt: now,
        } as SalesDocument
        tx.set(ref, clean(patientDoc))
        tx.set(docRefOf(payerId), clean(payerDoc))
      } else {
        const record: SalesDocument = {
          ...base, id: input.id, number: mainNumber, state, lines, subtotal, discountTotal, taxTotal, total, balance: total,
          issuer, issuedAt: issue ? now : existing?.issuedAt, issuedByName: issue ? user.fullName : existing?.issuedByName,
        } as SalesDocument
        tx.set(ref, clean(record))
      }

      if (isInvoice) {
        refs.forEach((r) => tx.set(claimRef(r), clean({ invoiceId: input.id, invoiceNumber: mainNumber, claimedAt: now, claimedByName: user.fullName })))
        releasedSnaps.forEach((snap, i) => {
          if ((snap.data() as ChargeClaim | undefined)?.invoiceId === input.id) tx.delete(claimRef(releasedRefs[i]))
        })
      }

      writeAudit(tx, user, {
        action: issue ? (splitting ? 'Invoice issued with payer split' : 'Invoice issued') : existing ? `${docLabel(input.type)} updated` : `${docLabel(input.type)} created`,
        entity: entityFor(input.type),
        entityId: input.id,
        reference: mainNumber ?? undefined,
        summary: `${input.account.name} — ${total.toFixed(2)}${splitting ? ` (${input.split!.percent}% to ${input.split!.payer.name})` : ''}`,
      })
      return { id: input.id, already: false, number: mainNumber }
    })
    return {
      ok: true,
      id: result.id,
      message: result.already
        ? `Already issued as ${result.number} — nothing was duplicated.`
        : issue
          ? `Invoice ${result.number} issued.`
          : `${docLabel(input.type)} ${result.number ?? 'draft'} saved.`,
    }
  } catch (error) {
    return failure(error, `The ${docLabel(input.type).toLowerCase()} could not be saved.`)
  }
}

/** Converts a quotation or order into an order/invoice without re-entering anything. Safe to retry. */
export async function convertDocument(user: User, config: BillingConfig, sourceId: string, toType: 'invoice' | 'order'): Promise<BillingResult> {
  try {
    const targetId = `conv-${sourceId}-${toType}`
    const id = await runTransaction(db, async (tx) => {
      const sourceRef = docRefOf(sourceId)
      const targetRef = docRefOf(targetId)
      const [sourceSnap, targetSnap] = await Promise.all([tx.get(sourceRef), tx.get(targetRef)])
      if (targetSnap.exists()) return targetId
      const source = sourceSnap.data() as SalesDocument | undefined
      if (!source) throw new Error('Document not found.')
      if (source.type === 'invoice') throw new Error('Invoices cannot be converted.')
      if (source.state === 'Converted') throw new Error(`Already converted to ${source.convertedToNumber ?? 'another document'}.`)
      if (source.state === 'Declined' || source.state === 'Cancelled') throw new Error(`A ${source.state.toLowerCase()} ${docLabel(source.type).toLowerCase()} cannot be converted.`)
      const refs = source.lines.map((l) => l.sourceRef).filter(Boolean) as string[]
      const claimSnaps = toType === 'invoice' ? await Promise.all(refs.map((r) => tx.get(claimRef(r)))) : []
      claimSnaps.forEach((snap, i) => {
        if (snap.exists()) throw new Error(`"${source.lines.find((l) => l.sourceRef === refs[i])?.description}" is already billed on ${(snap.data() as ChargeClaim).invoiceNumber ?? 'another invoice'}.`)
      })
      const plan = toType === 'order' ? await planNumbers(tx, config, 'order', 1) : null

      const now = new Date().toISOString()
      const date = todayLusaka()
      const number = plan ? plan.numbers[0] : null
      if (plan) commitNumbers(tx, plan, [targetId])
      const target: SalesDocument = {
        ...source,
        id: targetId,
        type: toType,
        number,
        state: toType === 'invoice' ? 'Draft' : 'Open',
        date,
        dueDate: toType === 'invoice' ? addDays(date, config.invoiceDefaults.termsDays) : undefined,
        validUntil: undefined,
        amountPaid: 0,
        amountCredited: 0,
        balance: source.total,
        convertedFromId: source.id,
        convertedFromNumber: source.number ?? undefined,
        convertedToId: undefined,
        convertedToNumber: undefined,
        issuer: undefined,
        issuedAt: undefined,
        issuedByName: undefined,
        staffId: user.id,
        staffName: user.fullName,
        createdAt: now,
        createdByName: user.fullName,
        updatedAt: now,
      }
      tx.set(targetRef, clean(target))
      tx.update(sourceRef, clean({ state: 'Converted', convertedToId: targetId, convertedToNumber: number ?? 'draft invoice', updatedAt: now }))
      if (toType === 'invoice') refs.forEach((r) => tx.set(claimRef(r), { invoiceId: targetId, invoiceNumber: null, claimedAt: now, claimedByName: user.fullName }))
      writeAudit(tx, user, {
        action: `${docLabel(source.type)} converted to ${toType}`,
        entity: entityFor(source.type),
        entityId: source.id,
        reference: source.number ?? undefined,
        summary: `${source.accountName} → ${number ?? 'draft invoice'}`,
      })
      return targetId
    })
    return { ok: true, id, message: toType === 'invoice' ? 'Converted to a draft invoice — review it and issue.' : 'Converted to an order.' }
  } catch (error) {
    return failure(error, 'The document could not be converted.')
  }
}

/** Voids an issued invoice that has no payments or credits applied. Releases its charges for rebilling. */
export async function voidInvoice(user: User, id: string, reason: string): Promise<BillingResult> {
  try {
    if (!reason.trim()) return { ok: false, error: 'A reason is required to void an invoice.' }
    await runTransaction(db, async (tx) => {
      const ref = docRefOf(id)
      const snap = await tx.get(ref)
      const invoice = snap.data() as SalesDocument | undefined
      if (!invoice || invoice.type !== 'invoice') throw new Error('Invoice not found.')
      if (invoice.state === 'Void') return
      if (invoice.state !== 'Issued') throw new Error('Only issued invoices can be voided. Delete drafts instead.')
      if (toN(invoice.amountPaid) > 0 || toN(invoice.amountCredited) > 0)
        throw new Error('Payments or credits are applied. Reverse the payments or issue a credit note instead of voiding.')
      let counterpart: SalesDocument | undefined
      if (invoice.split) counterpart = (await tx.get(docRefOf(invoice.split.counterpartId))).data() as SalesDocument | undefined
      const refs = invoice.lines.map((l) => l.sourceRef).filter(Boolean) as string[]
      const claimSnaps = await Promise.all(refs.map((r) => tx.get(claimRef(r))))
      const now = new Date().toISOString()
      tx.update(ref, { state: 'Void', balance: 0, voidedAt: now, voidedByName: user.fullName, voidReason: reason.trim(), updatedAt: now })
      const releaseClaims = !counterpart || counterpart.state === 'Void'
      if (releaseClaims) {
        claimSnaps.forEach((s, i) => {
          const owner = (s.data() as ChargeClaim | undefined)?.invoiceId
          if (owner === id || owner === invoice.split?.groupId || owner === invoice.split?.counterpartId) tx.delete(claimRef(refs[i]))
        })
      }
      writeAudit(tx, user, { action: 'Invoice voided', entity: 'Invoice', entityId: id, reference: invoice.number ?? undefined, summary: `${invoice.accountName} — ${invoice.total.toFixed(2)}`, reason: reason.trim() })
    })
    return { ok: true, id, message: 'Invoice voided. Its hospital charges can be billed again.' }
  } catch (error) {
    return failure(error, 'The invoice could not be voided.')
  }
}

/** Deletes an unissued draft invoice (never numbered) and releases its charge claims. */
export async function deleteDraft(user: User, id: string): Promise<BillingResult> {
  try {
    await runTransaction(db, async (tx) => {
      const ref = docRefOf(id)
      const snap = await tx.get(ref)
      const draft = snap.data() as SalesDocument | undefined
      if (!draft) return
      if (draft.type !== 'invoice' || draft.state !== 'Draft') throw new Error('Only draft invoices can be deleted.')
      const refs = draft.lines.map((l) => l.sourceRef).filter(Boolean) as string[]
      const claimSnaps = await Promise.all(refs.map((r) => tx.get(claimRef(r))))
      let source: DocumentReference | null = null
      let sourceDoc: SalesDocument | undefined
      if (draft.convertedFromId) {
        source = docRefOf(draft.convertedFromId)
        sourceDoc = (await tx.get(source)).data() as SalesDocument | undefined
      }
      tx.delete(ref)
      claimSnaps.forEach((s, i) => {
        if ((s.data() as ChargeClaim | undefined)?.invoiceId === id) tx.delete(claimRef(refs[i]))
      })
      if (source && sourceDoc && sourceDoc.convertedToId === id) {
        tx.update(source, { state: sourceDoc.type === 'order' ? 'Open' : 'Accepted', convertedToId: null, convertedToNumber: null })
      }
      writeAudit(tx, user, { action: 'Draft invoice deleted', entity: 'Invoice', entityId: id, summary: `${draft.accountName} — ${draft.total.toFixed(2)}` })
    })
    return { ok: true, id, message: 'Draft deleted.' }
  } catch (error) {
    return failure(error, 'The draft could not be deleted.')
  }
}

export async function setDocumentState(user: User, id: string, state: 'Sent' | 'Accepted' | 'Declined' | 'Cancelled', reason?: string): Promise<BillingResult> {
  try {
    await runTransaction(db, async (tx) => {
      const ref = docRefOf(id)
      const snap = await tx.get(ref)
      const record = snap.data() as SalesDocument | undefined
      if (!record || record.type === 'invoice') throw new Error('Document not found.')
      if (record.state === 'Converted') throw new Error('Converted documents cannot change status.')
      if (state === 'Cancelled' && record.type !== 'order') throw new Error('Only orders can be cancelled.')
      tx.update(ref, { state, updatedAt: new Date().toISOString() })
      writeAudit(tx, user, { action: `${docLabel(record.type)} marked ${state.toLowerCase()}`, entity: entityFor(record.type), entityId: id, reference: record.number ?? undefined, summary: record.accountName, reason })
    })
    return { ok: true, id }
  } catch (error) {
    return failure(error, 'The status could not be changed.')
  }
}

// ─── Recurring invoices ───

export function advanceDate(iso: string, frequency: RecurringFrequency) {
  if (frequency === 'Weekly') return addDays(iso, 7)
  const d = new Date(`${iso}T00:00:00`)
  const day = d.getDate()
  const months = frequency === 'Monthly' ? 1 : frequency === 'Quarterly' ? 3 : 12
  const target = new Date(d.getFullYear(), d.getMonth() + months, 1)
  const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  target.setDate(Math.min(day, last))
  return `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}-${String(target.getDate()).padStart(2, '0')}`
}

/**
 * Generates the invoice for the schedule's current run date. The invoice id is
 * derived from the schedule and run date, so a run can never be created twice.
 */
export async function runRecurring(user: User, config: BillingConfig, scheduleId: string): Promise<BillingResult> {
  try {
    const result = await runTransaction(db, async (tx) => {
      const recRef = doc(db, BCOL.recurring, scheduleId)
      const recSnap = await tx.get(recRef)
      const rec = recSnap.data() as RecurringInvoice | undefined
      if (!rec) throw new Error('Schedule not found.')
      if (rec.state !== 'Active') throw new Error(`This schedule is ${rec.state.toLowerCase()}.`)
      if (rec.endDate && rec.nextRun > rec.endDate) throw new Error('This schedule has passed its end date.')
      const invoiceId = `rec-${rec.id}-${rec.nextRun}`
      const invRef = docRefOf(invoiceId)
      const invSnap = await tx.get(invRef)
      if (invSnap.exists() || rec.runs.some((r) => r.runDate === rec.nextRun)) return { duplicate: true, invoiceId, number: (invSnap.data() as SalesDocument | undefined)?.number }
      const acctSnap = await tx.get(accountRef(rec.accountId))
      const plan = rec.autoIssue ? await planNumbers(tx, config, 'invoice', 1) : null

      const now = new Date().toISOString()
      const { lines, subtotal, discountTotal, taxTotal, total } = priced(rec.lines.map((l) => ({ ...l, sourceRef: undefined })))
      if (rec.autoIssue && toN(total) <= 0) throw new Error('The schedule total must be greater than zero to auto-issue.')
      const number = plan ? plan.numbers[0] : null
      if (plan) commitNumbers(tx, plan, [invoiceId])
      if (!acctSnap.exists()) {
        tx.set(accountRef(rec.accountId), clean({ id: rec.accountId, type: rec.accountType, name: rec.accountName, patientId: rec.patientId, bmcFileNumber: rec.bmcFileNumber, status: 'Active', createdAt: now, createdByName: user.fullName }))
      }
      const invoice: SalesDocument = {
        id: invoiceId, type: 'invoice', number, state: rec.autoIssue ? 'Issued' : 'Draft',
        accountId: rec.accountId, accountName: rec.accountName, accountType: rec.accountType,
        patientId: rec.patientId, patientName: rec.patientName, bmcFileNumber: rec.bmcFileNumber,
        date: rec.nextRun, dueDate: addDays(rec.nextRun, config.invoiceDefaults.termsDays), paymentTerms: config.invoiceDefaults.paymentTerms,
        notes: rec.notes, footer: config.invoiceDefaults.footer, staffId: user.id, staffName: user.fullName,
        lines, subtotal, discountTotal, taxTotal, total, amountPaid: 0, amountCredited: 0, balance: total,
        recurringId: rec.id, issuer: rec.autoIssue ? { business: config.business, appearance: config.appearance, regional: config.regional } : undefined,
        issuedAt: rec.autoIssue ? now : undefined, issuedByName: rec.autoIssue ? user.fullName : undefined,
        createdAt: now, createdByName: user.fullName, updatedAt: now,
      }
      tx.set(invRef, clean(invoice))
      const nextRun = advanceDate(rec.nextRun, rec.frequency)
      tx.update(recRef, clean({
        nextRun,
        state: rec.endDate && nextRun > rec.endDate ? 'Ended' : rec.state,
        runs: [...rec.runs, { runDate: rec.nextRun, invoiceId, at: now, byName: user.fullName }],
        updatedAt: now,
      }))
      writeAudit(tx, user, { action: 'Recurring invoice generated', entity: 'Recurring', entityId: rec.id, reference: number ?? undefined, summary: `${rec.name} — run ${rec.nextRun}` })
      return { duplicate: false, invoiceId, number }
    })
    return {
      ok: true,
      id: result.invoiceId,
      message: result.duplicate ? 'This run was already generated — no duplicate created.' : `Generated ${result.number ?? 'a draft invoice'}.`,
    }
  } catch (error) {
    return failure(error, 'The recurring invoice could not be generated.')
  }
}
